/**
 * Scroll reveals with weight: marked elements drop into place when they come
 * into view the way the in-game stamps land (`ui/motion/stamp.ts`).
 *
 * Owns: priming (hiding only the marked elements that start below the fold,
 * from script) and the gsap tween that plays when they enter. Must not:
 * hide anything in CSS. The resting state is the visible one, so a page whose
 * script is slow or dead is simply complete. Marks: `data-slam` (a stamp:
 * scale down and settle), `data-slam="rise"` (slide up), `data-slam="deal"`
 * (slide in from the side with a tilt); `--i` on the element staggers a row.
 * Under reduced motion nothing is primed or played.
 */
import { onBeforeUnmount, onMounted } from 'vue';

import { reducedMotion } from '@/engine/motion';
import { gsap } from '@/ui/motion/gsap';

type Kind = 'stamp' | 'rise' | 'deal';

/** Start poses; every tween ends at identity. Weights follow `stamp.ts`: the stamp over-scales then settles. */
const FROM: Readonly<Record<Kind, gsap.TweenVars>> = {
  stamp: { opacity: 0, scale: 1.45, rotation: -5, y: -12 },
  rise: { opacity: 0, y: 46 },
  deal: { opacity: 0, x: -60, rotation: -3 },
};

const EASE: Readonly<Record<Kind, string>> = { stamp: 'back.out(2.2)', rise: 'power3.out', deal: 'power3.out' };
const DURATION: Readonly<Record<Kind, number>> = { stamp: 0.5, rise: 0.7, deal: 0.65 };
/** Seconds between neighbours of a row (`--i`). */
const STAGGER_S = 0.07;
/** How much of an element must be visible before it plays. */
const ENTER_THRESHOLD = 0.15;

function kindOf(el: HTMLElement): Kind {
  const v = el.dataset.slam;
  return v === 'rise' || v === 'deal' ? v : 'stamp';
}

export function useSlam(getRoot: () => HTMLElement | null): void {
  let observer: IntersectionObserver | null = null;

  function play(el: HTMLElement): void {
    const kind = kindOf(el);
    const i = Number(getComputedStyle(el).getPropertyValue('--i')) || 0;
    gsap.to(el, {
      opacity: 1,
      scale: 1,
      rotation: 0,
      x: 0,
      y: 0,
      duration: DURATION[kind],
      ease: EASE[kind],
      delay: i * STAGGER_S,
      clearProps: 'opacity,transform',
    });
  }

  onMounted(() => {
    const root = getRoot();
    if (!root || reducedMotion() || typeof IntersectionObserver === 'undefined') return;
    const fold = root.clientHeight;
    const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-slam]'));
    observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          observer?.unobserve(e.target);
          play(e.target as HTMLElement);
        }
      },
      { root, threshold: ENTER_THRESHOLD },
    );
    for (const el of targets) {
      // Only what starts below the first screen is hidden: nothing the visitor already sees flickers.
      if (el.getBoundingClientRect().top < fold) continue;
      gsap.set(el, FROM[kindOf(el)]);
      observer.observe(el);
    }
  });

  onBeforeUnmount(() => observer?.disconnect());
}
